import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Icon,
  IconBtn,
  Input,
  Table,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { useViewport } from '@/lib/viewport';
import { useScope } from '@/lib/scope';
import type { Warehouse } from '@/domain';
import { formatNumber } from '@/lib/utils';

type ActiveFilter = 'all' | 'active' | 'inactive';

export function WarehousesPage() {
  const { isMobile } = useViewport();
  const { country, warehouses } = useScope();
  const [q, setQ] = useState('');
  const [filterActive, setFilterActive] = useState<ActiveFilter>('all');

  const inCountry = useMemo(
    () => warehouses.filter((w) => w.country === country),
    [warehouses, country],
  );

  const rows = useMemo(() => {
    let r = inCountry;
    if (filterActive !== 'all') {
      r = r.filter((w) => (filterActive === 'active' ? w.is_active : !w.is_active));
    }
    if (q) {
      const needle = q.toLowerCase();
      r = r.filter((w) =>
        [w.code, w.name, w.region].join(' ').toLowerCase().includes(needle),
      );
    }
    return r;
  }, [inCountry, filterActive, q]);

  const counts = {
    all: inCountry.length,
    active: inCountry.filter((w) => w.is_active).length,
    inactive: inCountry.filter((w) => !w.is_active).length,
  };

  const columns: Column<Warehouse>[] = [
    {
      header: 'Mã kho',
      width: 130,
      render: (r) => (
        <span
          style={{
            fontFamily: 'var(--bx-font-mono)',
            fontWeight: 600,
            color: 'var(--bx-text-dark)',
          }}
        >
          {r.code}
        </span>
      ),
    },
    {
      header: 'Tên kho',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 500, color: 'var(--bx-text-dark)' }}>{r.name}</div>
          <div style={{ fontSize: 11, color: 'var(--bx-text-muted)' }}>{r.region}</div>
        </div>
      ),
    },
    {
      header: 'Quốc gia',
      key: 'country',
      mono: true,
      priority: 2,
    },
    {
      header: 'Capacity/ngày',
      align: 'right',
      numeric: true,
      priority: 2,
      render: (r) => formatNumber(r.capacity_orders_per_day) + ' đơn',
    },
    {
      header: 'Trạng thái',
      render: (r) => (
        <Badge kind={r.is_active ? 'ACTIVE' : 'INACTIVE'}>
          {r.is_active ? 'ACTIVE' : 'INACTIVE'}
        </Badge>
      ),
    },
    {
      header: '',
      width: 100,
      render: () => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <IconBtn name="edit" title="Sửa" />
          <IconBtn name="trash" title="Xóa" />
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Quản trị', 'Kho']}
        title="Kho"
        subtitle={`Danh sách kho đang vận hành tại ${country} · ${counts.active}/${counts.all} hoạt động`}
        actions={
          <>
            <Button variant="outline" icon="download">
              Export CSV
            </Button>
            <Button variant="primary" icon="plus">
              Thêm kho
            </Button>
          </>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <Card padding={0}>
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--bx-border-muted)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ width: 280, minWidth: 200 }}>
              <Input
                icon="search"
                placeholder="Tìm theo mã, tên, vùng…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                size="sm"
              />
            </div>
            <div
              role="tablist"
              style={{
                display: 'inline-flex',
                gap: 4,
                padding: 4,
                background: 'var(--bx-surface-2)',
                borderRadius: 10,
              }}
            >
              {(
                [
                  { id: 'all', label: 'Tất cả', count: counts.all },
                  { id: 'active', label: 'Hoạt động', count: counts.active },
                  { id: 'inactive', label: 'Tạm dừng', count: counts.inactive },
                ] as { id: ActiveFilter; label: string; count: number }[]
              ).map((t) => {
                const active = filterActive === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setFilterActive(t.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      height: 26,
                      padding: '0 12px',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: active ? '#fff' : 'transparent',
                      color: active ? 'var(--bx-text-dark)' : 'var(--bx-text-body)',
                      fontWeight: active ? 600 : 500,
                      fontSize: 12.5,
                      boxShadow: active ? 'var(--bx-shadow-pill)' : 'none',
                      fontFamily: 'var(--bx-font-sans)',
                    }}
                  >
                    {t.label}
                    <span
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: 11,
                        color: active ? 'var(--bx-primary)' : 'var(--bx-text-muted)',
                      }}
                    >
                      {t.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" icon="filter">
              Bộ lọc khác
            </Button>
          </div>

          <Table
            columns={columns}
            rows={rows}
            getRowKey={(r) => r.id}
            emptyText="Không có kho khớp bộ lọc"
          />
        </Card>

        <Card
          title="Vì sao cần quản lý đây"
          subtitle="Ngữ cảnh cho admin mới"
          style={{ marginTop: 16 }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 16,
            }}
          >
            <Hint
              icon="cube"
              title="Phạm vi kho"
              body="Mọi config key có thể scope về 1 kho. Tạm dừng kho ở đây sẽ retire mọi giá trị scoped tương ứng."
            />
            <Hint
              icon="forecast"
              title="Forecast routing"
              body="Worker dự báo hằng ngày ghi 1 row forecast_results cho mỗi kho active. Kho inactive bị bỏ qua âm thầm."
            />
            <Hint
              icon="shield"
              title="Luật lao động"
              body="Quốc gia quyết định trần luật lao động (OT, giờ làm) áp lên mọi shift plan của kho đó."
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Hint({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: '#eef2ff',
          color: 'var(--bx-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={14} />
      </div>
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--bx-text-dark)',
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--bx-text-muted)', lineHeight: 1.5 }}>
          {body}
        </div>
      </div>
    </div>
  );
}
